import { Component, computed, effect, inject, OnDestroy, ViewChild } from '@angular/core';
import { AgpmRepository } from '../../../store/agpm-state/agpm-state.repository';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, FileRepresentation, SelectOption } from '../../../components/form-builder/form-builder.types';
import { getAgpmUpdateFormObject } from './utils/edit-agpm-form-object';
import { AgpmService } from '../agpm.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AgpmDetails, AgpmDto, TasksStatus } from '@/types';
import { CurrentTasksService } from '../../current-tasks/current-tasks.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CurrentTasksRepository } from '../../../store/current-tasks-state/current-tasks-state.repository';
import { TextareaModule } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { AgpmWorkflowBlocksService } from '../agpm-workflow-blocks.service';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { AuthorizationService } from '@/services/authorization.service';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { agpmCreateFormSchema } from '@/validations/agpm/create-form.validation';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { getAGPMDownloadDialog } from './utils/agpm-download.dialog-object';
import { fileUtils } from '@/utils/file.utils';
import { getAGPMUploadDialog } from './utils/agpm-upload.dialog-object';
import { url } from '@/utils/url.utils';

@Component({
  selector: 'sm-edit-notice',
  imports: [
    PageHeaderComponent,
    CommonModule,
    FormBuilderComponent,
    ButtonModule,
    TextareaModule,
    FormsModule,
    TranslateModule,
    BlockBuilderComponent,
    StatusComponent,
    DialogComponent,
  ],
  templateUrl: './edit-notice.component.html',
  styleUrl: './edit-notice.component.scss',
})
export class AgpmEditNoticeComponent implements OnDestroy {
  agpmRepository = inject(AgpmRepository);
  agpmService = inject(AgpmService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  currentTasksService = inject(CurrentTasksService);
  currentTasksRepository = inject(CurrentTasksRepository);
  agpmWorkflowBlocksService = inject(AgpmWorkflowBlocksService);
  authService = inject(AuthorizationService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  translate = inject(TranslateService);
  fileService = inject(FileService);

  acOptions: SelectOption[] = [];

  isPassForValidationDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  isDisabled = of(false);
  formObject: DynamicForm = getAgpmUpdateFormObject({
    store: this.agpmRepository,
    acOptions: this.acOptions,
    isDisabled: this.isDisabled,
    download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
    openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.AGPM),
  });

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  agpmDialogObject?: DialogObject;

  status!: TasksStatus;
  agpmDetails: AgpmDetails | null = null;

  paramMapSignal = toSignal(this.activatedRoute.paramMap, {
    initialValue: new Map() as unknown as ParamMap,
  });
  queryParamMapSignal = toSignal(this.activatedRoute.queryParamMap, {
    initialValue: new Map() as unknown as ParamMap,
  });

  controlBlocks: DynamicBlocks[] = [];

  comment = new BehaviorSubject<string>('');
  commentError = new BehaviorSubject<string | null>(null);

  loading = false;

  isSourceTask = false;

  agpmId = computed(() => {
    const paramMap = this.paramMapSignal();
    const id = paramMap.get('id');
    return id ? Number(id) : null;
  });

  isInspectMode = computed(() => {
    const paramMap = this.queryParamMapSignal();
    return paramMap.get('inspect') === 'true';
  });

  constructor() {
    effect(() => {
      const agpmId = this.agpmId();
      if (agpmId !== null) {
        this.isSourceTask = false;
        this.loadData(agpmId);
      }
    });
  }

  breadcrumb = [
    {
      label: 'agpm.breadcrumb.agpm',
      routerLink: ['/agpm-current-notice'],
    },
    {
      label: this.isInspectMode() ? 'agpm.breadcrumb.agpmConsult' : 'agpm.breadcrumb.agpmEdit',
      routerLink: ['agpm-edit-notice'],
    },
  ];

  onCommentChange(value: string) {
    this.comment.next(value);
    if (this.commentError.getValue()) {
      this.commentError.next(null);
    }
  }

  ngOnDestroy(): void {
    this.agpmRepository.reset();
    this.currentTasksRepository.reset();
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.agpmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.agpmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.AGPM_MODEL
    );
  }

  loadData(id: number) {
    forkJoin([this.agpmService.getOneAgpmDetails(id), this.contractingAuthorityService.getCurrentUserContractingAuthority()]).subscribe(
      ([response, acs]) => {
        this.agpmDetails = response;
        this.agpmRepository.setNested('response', {
          ...response.agpm,
          status: response.agpm.status,
          docs: response.agpm.docs,
        });
        this.agpmRepository.setNested('agpmServerObject', { ...response.agpm, acId: response.agpm.ac.id } as AgpmDto);
        this.isDisabled = of(
          (!response.isUpdatable && !!response.processInstanceId) || response.agpm.status == TasksStatus.PUBLIEE || this.isInspectMode()
        );
        this.acOptions = acs.map(ac => {
          return { name: ac.nom, code: ac.id };
        });
        this.formObject = getAgpmUpdateFormObject({
          store: this.agpmRepository,
          acOptions: this.acOptions,
          isDisabled: this.isDisabled,
          download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
          openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.AGPM),
          acDisabled: of(response.agpm.status !== TasksStatus.BROUILLON),
        });
        this.agpmRepository.setNested('dto', { ...response.agpm, acId: response.agpm.ac.id } as AgpmDto);
        this.agpmRepository.set('acsSelected', { name: response.agpm.ac.nom, code: response.agpm.ac.id });
        this.status = this.agpmRepository.getNested('response', 'status');
        this.controlBlocks =
          !response.processInstanceId && response.agpm.status === TasksStatus.BROUILLON && !this.isInspectMode()
            ? this.agpmWorkflowBlocksService.getDraftAgpmBlocks({
                refreshCount: response.agpm.nbrActualisation,
                save: () => this.update(),
                startWorkflow: () => this.submitForValidation(),
                accessHistory: () => this.router.navigate(['agpm-version-history', response.agpm.id]),
                loadingValidation: this.isPassForValidationDisabled$,
              })
            : (this.controlBlocks = this.agpmWorkflowBlocksService.getFlowableAgpmBlocks({
                refreshCount: response.agpm.nbrActualisation,
                task: response,
                validate: (value: string) => this.validate(Number(response.taskId), value, !response.isUpdatable),
                publish: () => this.publish(Number(response.taskId)),
                accessHistory: () => this.router.navigate(['agpm-version-history', response.agpm.id]),
                openDownloadDialog: () => this.validateAcStep('download'),
                openUploadDialog: () => this.validateAcStep('upload'),
                comment: this.comment,
                onCommentChange: (value: string) => this.onCommentChange(value),
                error: this.commentError,
                inspectMode: this.isInspectMode(),
              }));
      },
      err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.loading'),
        });
        this.router.navigate(['agpm-current-notice']);
      }
    );
  }

  update() {
    const result = agpmCreateFormSchema.safeParse(this.agpmRepository.get('dto'));
    if (result.success) {
      this.agpmRepository.clearErrors();
      this.loading = true;
      const agpmEditFormData = this.agpmRepository.getDtoFormData(this.isSourceTask);
      this.agpmService.updateAgpm(this.agpmRepository.getNested<number>('response', 'id'), agpmEditFormData).subscribe({
        next: data => {
          this.loadData(this.agpmRepository.getNested<number>('response', 'id'));
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('agpm.message.success.edited'),
          });
          this.loading = false;
          this.agpmRepository.setNested('response', data);
          this.agpmRepository.setNested('dto', 'files', []);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('agpm.message.error.edited'),
          });
          this.loading = false;
        },
      });
    } else {
      this.agpmRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  submitForValidation() {
    this.isPassForValidationDisabled$.next(true);
    this.agpmService.startWorkflow(this.agpmRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.passedForValidation'),
        });
        this.isPassForValidationDisabled$.next(false);
        this.router.navigate(['agpm-current-notice']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.passedForValidation'),
        });
        this.isPassForValidationDisabled$.next(false);
      },
    });
  }

  validateAcStep(variant: 'download' | 'upload') {
    if (variant === 'download')
      this.agpmDialogObject = getAGPMDownloadDialog({
        store: this.agpmRepository,
        validate: () => {
          this.agpmService
            .downloadAgpmModel({
              agpmId: this.agpmRepository.getNested('response', 'id'),
              agpmVer: this.agpmRepository.getNested('response', 'version'),
              signataire: this.agpmRepository.getNested('download', 'signPerson'),
              fonction: this.agpmRepository.getNested('download', 'function'),
            })
            .subscribe((blob: Blob) => {
              fileUtils.triggerBlobDownload(blob, `${this.agpmRepository.getNested('response', 'ref')}_modele.pdf`);
            });
          this.dialogComponent.toggleVisible();
        },
      });
    else {
      this.agpmDialogObject = getAGPMUploadDialog({
        store: this.agpmRepository,
        validate: () => {
          const modelAgpmFileAsArray = this.agpmRepository.getNested<File[]>('upload', 'modelAgpmFile');
          if (modelAgpmFileAsArray.length === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Fichier manquant',
              detail: 'Veuillez sélectionner un fichier avant de passer pour validation',
            });
            return;
          }
          const formData = new FormData();
          const modelAgpmFile = modelAgpmFileAsArray[0];
          formData.append('validate', 'true');
          formData.append('comment', this.comment.getValue());
          formData.append('modelAgpmFile', modelAgpmFile);
          if (this.agpmDetails?.taskId) {
            this.agpmService.uploadAgpmModel(this.agpmDetails?.taskId, formData).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Succès',
                  detail: 'Le AGPM a été passé pour validation avec succès',
                });
                this.dialogComponent.toggleVisible();
                url.reloadComponent(this.router);
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: err.error.message || "Une erreur est survenue lors de la passage du AGPM l'étape suivante",
                });
              },
            });
          }
        },
      });
    }
    this.dialogComponent.toggleVisible();
  }

  validate(taskId: number, validationValue: string, commentIsRequired: boolean) {
    this.commentError.next('');
    this.currentTasksRepository.setNested('dto', 'validate', validationValue, true);
    if (!validationValue) {
      if (this.comment.getValue().length === 0 && commentIsRequired) {
        this.commentError.next('Veuillez saisir un commentaire');
        return;
      }
    }
    const agpmEditFormData = this.agpmRepository.getDtoFormData(true);
    agpmEditFormData.append('comment', this.currentTasksRepository.getNested('dto', 'comment'));
    if (commentIsRequired) {
      agpmEditFormData.append('validate', validationValue.toString());
    }

    this.currentTasksService.validateAgpm(taskId, agpmEditFormData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: validationValue
            ? this.translate.instant('agpm.message.success.validated')
            : this.translate.instant('agpm.message.success.rejected'),
        });
        this.router.navigate(['agpm-current-notice']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.passedForValidation'),
        });
      },
    });
  }

  publish(taskId: number) {
    const formData = new FormData();
    formData.append('comment', this.currentTasksRepository.getNested('dto', 'comment'));
    this.currentTasksService.validateAgpm(taskId, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.published'),
        });
        this.router.navigate(['agpm-current-notice']);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.published'),
        });
      },
    });
  }
}
